import React, { useState, useEffect, useRef } from 'react';
import { attendanceAPI, employeesAPI, companiesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import ExportButton from '../components/ExportButton';
import { 
  Clock, 
  MapPin, 
  Camera, 
  LogIn, 
  LogOut,
  Calendar,
  Users,
  CheckCircle2,
  XCircle,
  Loader2
} from 'lucide-react';

const Attendance = () => {
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [todaySummary, setTodaySummary] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [loading, setLoading] = useState(true);
  const [punchDialogOpen, setPunchDialogOpen] = useState(false);
  const [punchType, setPunchType] = useState('in');
  const [location, setLocation] = useState(null);
  const [photoData, setPhotoData] = useState(null);
  const [punching, setPunching] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedCompany]);

  const fetchData = async () => {
    try {
      const [employeesRes, companiesRes] = await Promise.all([
        employeesAPI.getAll(selectedCompany ? { company_id: selectedCompany } : {}),
        companiesAPI.getAll()
      ]);
      setEmployees(employeesRes.data);
      setCompanies(companiesRes.data);

      if (selectedCompany) {
        const summaryRes = await attendanceAPI.getTodaySummary(selectedCompany);
        setTodaySummary(summaryRes.data);
      }

      const today = new Date().toISOString().split('T')[0];
      const attendanceRes = await attendanceAPI.getAll({ 
        date: today,
        ...(selectedCompany && { company_id: selectedCompany })
      });
      setAttendance(attendanceRes.data);
    } catch (error) {
      console.error('Failed to fetch attendance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraActive(true);
      }
    } catch (error) {
      toast.error('Failed to access camera');
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setCameraActive(false);
    setPhotoData(null);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      canvasRef.current.width = videoRef.current.videoWidth;
      canvasRef.current.height = videoRef.current.videoHeight;
      context.drawImage(videoRef.current, 0, 0);
      const dataUrl = canvasRef.current.toDataURL('image/jpeg', 0.8);
      setPhotoData(dataUrl.split(',')[1]);
    }
  };

  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => reject(error),
        { enableHighAccuracy: true }
      );
    });
  };

  const openPunchDialog = async (type) => {
    setPunchType(type);
    setPunchDialogOpen(true);
    try {
      const loc = await getLocation();
      setLocation(loc);
    } catch (error) {
      toast.error('Please enable location access');
    }
  };

  const handlePunch = async () => {
    if (!selectedEmployee) {
      toast.error('Please select an employee');
      return;
    }
    if (!location) {
      toast.error('Location is required');
      return;
    }

    setPunching(true);
    try {
      await attendanceAPI.punch({
        employee_id: selectedEmployee,
        punch_type: punchType,
        latitude: location.latitude,
        longitude: location.longitude,
        photo_base64: photoData
      });
      toast.success(`Punched ${punchType} successfully!`);
      setPunchDialogOpen(false);
      stopCamera();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Punch failed');
    } finally {
      setPunching(false);
    }
  };

  const closePunchDialog = () => {
    setPunchDialogOpen(false);
    stopCamera();
    setPhotoData(null);
    setLocation(null);
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown';
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Attendance</h1>
          <p className="text-muted-foreground">Track employee attendance with GPS & photo</p>
        </div>
        <div className="flex gap-2">
          <ExportButton endpoint="/reports/attendance/export" filename="attendance" testId="export-attendance" />
          <Button 
            variant="outline" 
            onClick={() => openPunchDialog('in')}
            data-testid="punch-in-btn"
          >
            <LogIn size={18} className="mr-2" />
            Punch In
          </Button>
          <Button 
            onClick={() => openPunchDialog('out')}
            data-testid="punch-out-btn"
          >
            <LogOut size={18} className="mr-2" />
            Punch Out
          </Button>
        </div>
      </div>

      {/* Today's Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="summary-date">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Calendar className="text-primary" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Today</p>
                <p className="font-semibold">{new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'short',
                  day: 'numeric'
                })}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="summary-total">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Users className="text-blue-500" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Employees</p>
                <p className="text-2xl font-bold">{employees.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="summary-present">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2 className="text-emerald-500" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Present</p>
                <p className="text-2xl font-bold text-emerald-500">
                  {todaySummary?.present || attendance.filter(a => a.punch_in).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="summary-absent">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <XCircle className="text-red-500" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Absent</p>
                <p className="text-2xl font-bold text-red-500">
                  {todaySummary?.absent || (employees.length - attendance.filter(a => a.punch_in).length)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={selectedCompany} onValueChange={setSelectedCompany}>
          <SelectTrigger className="w-64" data-testid="company-filter">
            <SelectValue placeholder="Filter by company" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Companies</SelectItem>
            {companies.map(company => (
              <SelectItem key={company.id} value={company.id}>
                {company.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Attendance Table */}
      <Card>
        <CardHeader>
          <CardTitle>Today's Attendance Records</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Punch In</TableHead>
                <TableHead>Punch Out</TableHead>
                <TableHead>Total Hours</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Photos</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attendance.length > 0 ? (
                attendance.map((record) => (
                  <TableRow key={record.id} data-testid={`attendance-row-${record.id}`}>
                    <TableCell className="font-medium">
                      {getEmployeeName(record.employee_id)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-muted-foreground" />
                        {formatTime(record.punch_in)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-muted-foreground" />
                        {formatTime(record.punch_out)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {record.total_hours ? `${record.total_hours.toFixed(2)} hrs` : '-'}
                    </TableCell>
                    <TableCell className="text-xs">
                      {record.punch_in_location?.latitude ? (
                        <a
                          href={`https://www.google.com/maps?q=${record.punch_in_location.latitude},${record.punch_in_location.longitude}`}
                          target="_blank" rel="noreferrer"
                          className="text-primary hover:underline flex items-center gap-1"
                          data-testid={`map-link-${record.id}`}
                        >
                          <MapPin size={12} />
                          {record.punch_in_location.latitude.toFixed(4)},{record.punch_in_location.longitude.toFixed(4)}
                          {record.punch_in_location.inside_geofence === false && (
                            <Badge variant="destructive" className="ml-1 text-[10px]">outside</Badge>
                          )}
                        </a>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {record.punch_in_photo && (
                          <a href={record.punch_in_photo} target="_blank" rel="noreferrer" title="Punch-in photo">
                            <img src={record.punch_in_photo} alt="in" className="h-10 w-10 rounded object-cover border" data-testid={`photo-in-${record.id}`} />
                          </a>
                        )}
                        {record.punch_out_photo && (
                          <a href={record.punch_out_photo} target="_blank" rel="noreferrer" title="Punch-out photo">
                            <img src={record.punch_out_photo} alt="out" className="h-10 w-10 rounded object-cover border" data-testid={`photo-out-${record.id}`} />
                          </a>
                        )}
                        {!record.punch_in_photo && !record.punch_out_photo && <span className="text-muted-foreground text-xs">—</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={record.punch_in ? 'success' : 'secondary'}>
                        {record.status || 'present'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No attendance records for today
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Punch Dialog */}
      <Dialog open={punchDialogOpen} onOpenChange={closePunchDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {punchType === 'in' ? 'Punch In' : 'Punch Out'}
            </DialogTitle>
            <DialogDescription>
              Capture your attendance with photo and location
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Employee</label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger data-testid="punch-employee-select">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} ({emp.employee_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Location */}
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <MapPin size={16} className="text-primary" />
                <span className="text-sm font-medium">Location</span>
              </div>
              {location ? (
                <p className="text-sm text-muted-foreground">
                  Lat: {location.latitude.toFixed(6)}, Lng: {location.longitude.toFixed(6)}
                </p>
              ) : (
                <p className="text-sm text-amber-500">Fetching location...</p>
              )}
            </div>

            {/* Camera */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Camera size={16} className="text-primary" />
                <span className="text-sm font-medium">Photo (Optional)</span>
              </div>
              <div className="relative aspect-video bg-muted rounded-lg overflow-hidden">
                {cameraActive ? (
                  <>
                    <video 
                      ref={videoRef} 
                      autoPlay 
                      playsInline 
                      muted
                      className="w-full h-full object-cover"
                    />
                    {photoData && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <CheckCircle2 className="text-emerald-500" size={48} />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Button variant="outline" onClick={startCamera}>
                      <Camera size={18} className="mr-2" />
                      Start Camera
                    </Button>
                  </div>
                )}
              </div>
              <canvas ref={canvasRef} className="hidden" />
              {cameraActive && !photoData && (
                <Button variant="outline" className="w-full" onClick={capturePhoto}>
                  Capture Photo
                </Button>
              )}
            </div>

            <Button 
              className="w-full" 
              onClick={handlePunch}
              disabled={!selectedEmployee || !location || punching}
              data-testid="confirm-punch-btn"
            >
              {punching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm {punchType === 'in' ? 'Punch In' : 'Punch Out'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Attendance;
